/**
 * @astrology/i18n — shared message keys + locale resolution.
 *
 * Three surfaces consume this package:
 *  - apps/webapp (Next.js + next-intl)
 *  - apps/www    (Astro built-in i18n)
 *  - apps/tma    (Vite + lightweight runtime)
 *
 * Source of truth = `src/locales/<locale>.json`. RU is canonical, EN/ES are
 * full translations of the same keys (NEVER ship partial files — JSON shape
 * is type-checked via the `Messages` type derived from `ru.json`).
 */

import en from "./locales/en.json";
import es from "./locales/es.json";
import ru from "./locales/ru.json";

export const LOCALES = ["ru", "en", "es"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "ru";

/** Type of the canonical RU message tree. EN/ES must satisfy this shape. */
export type Messages = typeof ru;

const TABLE: Readonly<Record<Locale, Messages>> = {
  ru,
  en: en as Messages,
  es: es as Messages,
};

/** Return the message bundle for a locale; falls back to RU. */
export function getMessages(locale: Locale | string | undefined): Messages {
  if (locale && (LOCALES as readonly string[]).includes(locale)) {
    return TABLE[locale as Locale];
  }
  return TABLE[DEFAULT_LOCALE];
}

/**
 * Dotted-path message reader (e.g. `t("nav.today")`). Returns the raw string
 * or the path itself when the key is missing (helps surface missing keys in
 * UI during development).
 */
export function tFor(locale: Locale | string | undefined) {
  const messages = getMessages(locale);
  return function t(path: string): string {
    const parts = path.split(".");
    let cursor: unknown = messages;
    for (const part of parts) {
      if (cursor && typeof cursor === "object" && part in (cursor as object)) {
        cursor = (cursor as Record<string, unknown>)[part];
      } else {
        return path;
      }
    }
    return typeof cursor === "string" ? cursor : path;
  };
}

/** Parse a `/en/...` or `/es/...` URL prefix → locale. Root path = RU. */
export function localeFromPathname(pathname: string): Locale {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (seg === "en" || seg === "es") return seg;
  return DEFAULT_LOCALE;
}

/** Strip the locale prefix to get the canonical pathname (always leading `/`). */
export function stripLocale(pathname: string): string {
  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] === "en" || seg[0] === "es") {
    const rest = seg.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return pathname || "/";
}

/** Build a locale-prefixed URL (RU stays at root by product decision). */
export function withLocale(pathname: string, locale: Locale): string {
  const canonical = stripLocale(pathname);
  if (locale === DEFAULT_LOCALE) return canonical;
  return canonical === "/" ? `/${locale}` : `/${locale}${canonical}`;
}

/** Intl-based date formatter. */
export function formatDate(date: Date, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "ru" ? "ru-RU" : locale === "es" ? "es-ES" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

/** Intl-based number formatter. */
export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(
    locale === "ru" ? "ru-RU" : locale === "es" ? "es-ES" : "en-US",
  ).format(value);
}
